import type { CSSProperties } from 'react'
import { useState } from 'react'
import { ArrowRight, Award, Check, RotateCcw, X } from 'lucide-react'
import type { Cursus } from '../lib/types'
import Snippers from './Snippers'

/* ===========================================================================
 *  De toets
 *
 *  Anders dan de werkvormen in de modules: hier komt het antwoord NIET
 *  meteen. Je maakt de tien vragen, en daarna zie je wat er goed was.
 *
 *  Dat is geen strengheid om de strengheid. Met een antwoord na elke vraag is
 *  het geen toets meer maar een oefening -- je past je volgende antwoord aan
 *  op wat je net leerde, en aan het eind weet niemand wat je wist. De
 *  oefening staat in de modules; dit is de meting.
 *
 *  Zakken kost niets. Je mag hem zo vaak doen als je wilt, en de database
 *  bewaart de hoogste score en het aantal pogingen apart (migratie 0147) --
 *  dus het blijft eerlijk zonder dat het straft.
 * =========================================================================== */

export default function Toets({
  cursus, eerderGeslaagd, naam, opKlaar, opNaarOverzicht,
}: {
  cursus: Cursus
  eerderGeslaagd: boolean
  naam: string
  /** score 0..100 en of het gehaald is. */
  opKlaar: (score: number, geslaagd: boolean) => void
  opNaarOverzicht: () => void
}) {
  const [bij, setBij] = useState(0)
  const [antwoorden, setAntwoorden] = useState<(number | null)[]>(
    () => cursus.toets.map(() => null))
  const [af, setAf] = useState(false)

  const vraag = cursus.toets[bij]
  const gekozen = antwoorden[bij] ?? null

  function kies(i: number) {
    setAntwoorden((was) => {
      const nu = [...was]
      nu[bij] = i
      return nu
    })
  }

  function verder() {
    if (bij + 1 < cursus.toets.length) { setBij(bij + 1); return }

    const goed = cursus.toets.filter((v, i) => antwoorden[i] === v.goed).length
    const score = Math.round((goed / cursus.toets.length) * 100)
    setAf(true)
    opKlaar(score, score >= cursus.slaagpercentage)
  }

  function opnieuw() {
    setAntwoorden(cursus.toets.map(() => null))
    setBij(0)
    setAf(false)
  }

  /* ---------------------------------------------------------------- *
   *  De uitslag
   * ---------------------------------------------------------------- */

  if (af) {
    const goed = cursus.toets.filter((v, i) => antwoorden[i] === v.goed).length
    const score = Math.round((goed / cursus.toets.length) * 100)
    const gehaald = score >= cursus.slaagpercentage

    return (
      <div className="toets-uitslag">
        {gehaald && <Snippers />}

        <div className={`rond pop ${gehaald ? 'gehaald' : 'gezakt'}`}>
          {score}%
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'var(--fs-groot)', marginBottom: 8 }}>
            {gehaald ? 'Gehaald' : 'Nog niet gehaald'}
          </h2>
          <p style={{ color: 'var(--text-zacht)', fontSize: 'var(--fs-klein)' }}>
            {goed} van de {cursus.toets.length} goed.{' '}
            {gehaald
              ? 'Je hebt de cursus afgerond.'
              : `Je hebt ${cursus.slaagpercentage}% nodig. Lees de misgegane vragen hieronder terug en doe hem gerust nog een keer — dat telt niet tegen je.`}
          </p>
        </div>

        {gehaald && (
          <div className="bewijs opkomst" style={{ '--vertraging': '280ms' } as CSSProperties}>
            <div className="zegel"><Award size={34} /></div>
            <h2>{cursus.titel}</h2>
            <div className="naam">{naam}</div>
            <div className="cijfer">{score}<span>%</span></div>
            <div className="regel">
              Afgerond op {vandaag()} · versie {cursus.versie} van de cursus
            </div>
            <div className="regel" style={{ marginTop: 10, color: 'var(--text-dim)' }}>
              Dit staat bij je dossier. Je hoeft niets af te drukken.
            </div>
          </div>
        )}

        <div className="kopje">Je antwoorden</div>

        <div className="nakijken">
          {cursus.toets.map((v, i) => {
            const goedGedaan = antwoorden[i] === v.goed
            return (
              <div key={v.id} className={`nakijk-regel ${goedGedaan ? 'goed' : 'mis'}`}>
                {goedGedaan ? <Check size={17} /> : <X size={17} />}
                <span>
                  <span className="vraagje">{i + 1}. {v.vraag}</span>
                  {!goedGedaan && (
                    <span className="uitleg">
                      <strong>Het goede antwoord: </strong>{v.opties[v.goed]}<br />
                      {v.uitleg}
                    </span>
                  )}
                  {goedGedaan && <span className="uitleg">{v.uitleg}</span>}
                </span>
              </div>
            )
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="knop" onClick={opnieuw}>
            <RotateCcw size={16} /> Toets opnieuw
          </button>
          <button className="knop hoofd" onClick={opNaarOverzicht}>
            Naar het overzicht <ArrowRight size={16} />
          </button>
        </div>
      </div>
    )
  }

  /* ---------------------------------------------------------------- *
   *  De vragen
   * ---------------------------------------------------------------- */

  if (!vraag) return null

  return (
    <div className="toets">
      {eerderGeslaagd && (
        <div className="uitslag grijs">
          <Award size={17} />
          <span>
            Je hebt deze toets eerder gehaald. Nog een keer doen mag; je
            hoogste score blijft staan.
          </span>
        </div>
      )}

      <div className="balkje">
        <span>Vraag {bij + 1} van {cursus.toets.length}</span>
        <span>{cursus.slaagpercentage}% nodig om te slagen</span>
      </div>

      <div className="baan" aria-hidden>
        <i
          style={{
            width: `${(bij / cursus.toets.length) * 100}%`,
            background: 'linear-gradient(90deg, var(--brand), #ffd97a)',
          }}
        />
      </div>

      <div key={vraag.id} className="opzij" style={{ display: 'grid', gap: 16 }}>
          {vraag.situatie && <div className="situatie">{vraag.situatie}</div>}
          <div className="vraag">{vraag.vraag}</div>

          <div className="opties">
            {vraag.opties.map((optie, i) => (
              <button
                key={i}
                type="button"
                className={`optie ${gekozen === i ? 'goed' : ''}`}
                onClick={() => kies(i)}
              >
                <span className="merk">{LETTERS[i]}</span>
                <span>{optie}</span>
              </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        {bij > 0 && (
          <button className="knop" onClick={() => setBij(bij - 1)}>Vorige</button>
        )}
        <button
          className="knop hoofd"
          style={{ marginLeft: 'auto' }}
          onClick={verder}
          disabled={gekozen === null}
        >
          {bij + 1 === cursus.toets.length ? 'Nakijken' : 'Volgende'}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}

const LETTERS = ['A', 'B', 'C', 'D', 'E']

function vandaag(): string {
  return new Date().toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}
